import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Printer,
  FileText,
  Paperclip,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { getProforma } from '../../../../services/finance_services/proformaService';
import { getProformaAttachments, downloadProformaAttachment } from '../../../../services/finance_attachment_services/proformaAttachmentService';
import { exportProformaPDF, exportProformaExcel } from '../../../../services/export_services/proformaExportService';
import { formatDate, formatCurrency, formatFileSize } from '../../../../utils/formatUtils';
import useApi from '../../../../hooks/useApi';

const InviterProformaDetailPage = () => {
  const { invitationId, id } = useParams();
  const navigate = useNavigate();
  const [proforma, setProforma] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [notification, setNotification] = useState(null);
  const [permissionLevel, setPermissionLevel] = useState('view');

  // Use the useApi hook for fetching proforma
  const { loading, error, callApi: fetchProformaData } = useApi(getProforma, {showSuccessToast: false});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const proformaData = await fetchProformaData(id, invitationId);
        if (proformaData) {
          setProforma(proformaData);
          // Extract permission level from the response if available
          if (proformaData.permission_level) {
            setPermissionLevel(proformaData.permission_level);
          }
        }

        // Fetch attachments separately (not using useApi for this secondary call)
        const attachmentsData = await getProformaAttachments(id, invitationId);
        setAttachments(attachmentsData?.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [id, invitationId, fetchProformaData]);

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const result = await exportProformaExcel(id, invitationId);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Excel download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Excel download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadClick = async () => {
    setDownloading(true);
    try {
      const result = await exportProformaPDF(id, invitationId);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      setNotification({ type: 'loading', message: 'Downloading attachment...' });
      const result = await downloadProformaAttachment(id, attachmentId, invitationId);
      
      if (!result.success) {
        throw new Error(result.error || 'Download failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Check if user can download based on permission level
  const canDownload = permissionLevel === 'view_download' || permissionLevel === 'full_management';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg flex items-center">
        <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
        <div>
          <h3 className="font-medium">Error loading proforma details</h3>
          <p className="text-sm text-red-600">{error}</p>
          <button 
            onClick={() => navigate(`/dashboard/collaborator/${invitationId}/proformas`)}
            className="mt-3 text-sm text-primary-600 hover:text-primary-800"
          >
            Return to proforma list
          </button>
        </div>
      </div>
    );
  }
  
  if (!proforma) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-6 rounded-lg">
        <h3 className="font-medium">Proforma not found</h3>
        <p className="text-sm">The proforma you're looking for doesn't exist or has been deleted.</p>
        <button 
          onClick={() => navigate(`/dashboard/collaborator/${invitationId}/proformas`)}
          className="mt-3 text-sm text-primary-600 hover:text-primary-800"
        >
          Return to proforma list
        </button>
      </div>
    );
  }

  // Financial calculations
  const items = proforma.items || [];
  const subtotal = items.reduce((sum, item) => 
    sum + (item.unit_price * item.quantity - (item.discount_amount || 0)), 0
  );
  const totalDiscount = items.reduce((sum, item) => 
    sum + (item.discount_amount || 0), 0
  );
  const totalTax = items.reduce((sum, item) => {
    const taxable = (item.unit_price * item.quantity - (item.discount_amount || 0));
    return sum + (taxable * (item.tax_rate || 0) / 100);
  }, 0);
  const grandTotal = subtotal + totalTax;
  const validityDays = proforma.expiry_date && proforma.date_issued 
    ? Math.ceil((new Date(proforma.expiry_date) - new Date(proforma.date_issued)) / (1000*60*60*24)) 
    : null;

  return (
    <div className="space-y-6">
      {/* Notification toast */}
      {notification && (
        <div className={`fixed top-5 right-5 z-50 flex items-center p-4 rounded-lg shadow-md ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {notification.type === 'success' && <CheckCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'loading' && 
            <div className="h-5 w-5 mr-2 border-2 border-t-primary-600 border-r-primary-600 border-b-primary-100 border-l-primary-100 rounded-full animate-spin"></div>
          }
          <span>{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header with back button and actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate(`/dashboard/collaborator/${invitationId}/proformas`)}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Proformas
        </button>
        
        {canDownload && (
          <div className="flex gap-3">
            <button
              onClick={handleDownloadClick}
              disabled={downloading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-75"
            >
              {downloading ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-t-primary-300 border-r-primary-300 border-b-white border-l-white rounded-full animate-spin"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </>
              )}
            </button>
            
            <button
              onClick={handleDownloadExcel}
              disabled={downloadingExcel}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-75"
            >
              {downloadingExcel ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-t-green-300 border-r-green-300 border-b-white border-l-white rounded-full animate-spin"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download Excel
                </>
              )}
            </button>
            
            <button 
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>
        )}
      </div>

      {/* Report Preview Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 print:shadow-none print:border-none">
        {/* Report Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Proforma #{proforma.proforma_number || proforma.id}</h2>
              <p className="text-sm text-gray-500">{proforma.title}</p>
              <p className="text-sm text-gray-500">Created on {formatDate(proforma.date_created)}</p>
            </div>
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium flex-shrink-0">
              {proforma.status_display}
            </div>
          </div>
        </div>

        {/* Client and Proforma Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 border">
            <div className="font-semibold text-gray-700 mb-1">Client Information</div>
            <div><span className="font-semibold">Name:</span> {proforma.client}</div>
            <div><span className="font-semibold">Email:</span> {proforma.client_email}</div>
            <div><span className="font-semibold">Phone:</span> {proforma.client_phone}</div>
            <div><span className="font-semibold">Address:</span> {proforma.client_address}</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 border">
            <div className="font-semibold text-gray-700 mb-1">Proforma Details</div>
            <div><span className="font-semibold">Status:</span> {proforma.status_display}</div>
            <div><span className="font-semibold">Issued:</span> {formatDate(proforma.date_issued)}</div>
            <div><span className="font-semibold">Expiry:</span> {formatDate(proforma.expiry_date)}</div>
            {validityDays !== null && (
              <div><span className="font-semibold">Validity:</span> {validityDays} days</div>
            )}
            <div><span className="font-semibold">Items:</span> {items.length}</div>
          </div>
        </div>
        
        {/* Financial Breakdown */}
        <div className="bg-white rounded-lg shadow border p-4 mb-8">
          <div className="font-semibold text-gray-700 mb-2">Financial Breakdown</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-semibold">Subtotal:</span>
              <br />{formatCurrency(subtotal, 'FCFA')}
            </div>
            <div>
              <span className="font-semibold">Total Discount:</span>
              <br />{formatCurrency(totalDiscount, 'FCFA')}
            </div>
            <div>
              <span className="font-semibold">Total Tax:</span>
              <br />{formatCurrency(totalTax, 'FCFA')}
            </div>
            <div>
              <span className="font-semibold">Grand Total:</span>
              <br /><span className="font-bold text-lg">{formatCurrency(grandTotal, 'FCFA')}</span>
            </div>
          </div>
        </div>
        
        {/* Items Table */}
        <div className="bg-white rounded-lg shadow border p-4 mb-8">
          <div className="font-semibold mb-2">Items</div>
          <div className="overflow-x-auto">
            <table className="w-full border rounded text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Qty</th>
                  <th className="px-3 py-2">Unit Price</th>
                  <th className="px-3 py-2">Discount</th>
                  <th className="px-3 py-2">Tax Rate</th>
                  <th className="px-3 py-2">Tax Amt</th>
                  <th className="px-3 py-2">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map(item => {
                    const itemDiscount = item.discount_amount || 0;
                    const taxable = (item.unit_price * item.quantity - itemDiscount);
                    const taxAmt = taxable * (item.tax_rate || 0) / 100;
                    const lineTotal = taxable + taxAmt;
                    
                    return (
                      <tr key={item.id} className="border-t border-gray-100">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2">{item.quantity}</td>
                        <td className="px-3 py-2">{formatCurrency(item.unit_price, 'FCFA')}</td>
                        <td className="px-3 py-2">{formatCurrency(itemDiscount, 'FCFA')}</td>
                        <td className="px-3 py-2">{item.tax_rate || 0}%</td>
                        <td className="px-3 py-2">{formatCurrency(taxAmt, 'FCFA')}</td>
                        <td className="px-3 py-2">{formatCurrency(lineTotal, 'FCFA')}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-4">No items.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Terms and Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="font-semibold text-gray-700 mb-1">Terms & Conditions</div>
            <div className="text-gray-800 whitespace-pre-line">
              {proforma.terms_and_conditions || 'None'}
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="font-semibold text-gray-700 mb-1">Notes</div>
            <div className="text-gray-800 whitespace-pre-line">
              {proforma.notes || 'None'}
            </div>
          </div>
        </div>

        {/* Attachments Section */}
        <div>
          <h3 className="text-lg font-medium mb-4">Attachments</h3>
          {attachments.length === 0 ? (
            <div className="bg-gray-50 p-8 text-center rounded-lg">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p className="text-gray-500">No attachments for this proforma</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {attachments.map(attachment => (
                <div key={attachment.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start">
                    <Paperclip className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{attachment.title}</p>
                      <p className="text-xs text-gray-500">
                        {attachment.file_size 
                          ? formatFileSize(attachment.file_size)
                          : 'Size unknown'}
                      </p>
                      {canDownload && (
                        <button
                          onClick={() => handleDownloadAttachment(attachment.id)}
                          className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

InviterProformaDetailPage.defaultProps = {
  permissionLevel: 'view',
};

export default InviterProformaDetailPage;